import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/components/ModalContainer";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, memo } from "react";
import { useAppStore } from "@/controller";
import { getDefaultTranscriptionLanguage, LANGUAGES } from "@/languages";
import { getPlatform, getPlatformRuntime } from "@/platform";
import { useRouter } from "@/_router";
import {
  recoverInterruptedLiveRecording,
  useLiveRecorder,
} from "@/recording/useLiveRecorder";
import toast from "react-hot-toast";
import type {
  RecordingHealth,
  StopRecordingError,
} from "@/recording/useLiveRecorder";
import { PlayIcon, PauseIcon, ChevronDownIcon } from "@/components/Icons";
import { actions, desktop } from "@/controller";
import { getLiveRecordingRecoveryForTranscript } from "@/recording/recovery";
import { onRecordingAppStateChange } from "@/recording/ports";
import { attachRecordingNetworkListeners, attachRecordingVisibilityListener } from "@/recording/lifecycle";
import { useSharedDesktopHost } from "@/models/desktop-host-context";
import { createActionError } from "@/client-error";

// Finalize must be bounded and idempotent from the client's perspective: the
// request runs behind the "saving" spinner, so it gets a per-attempt timeout
// plus bounded retries (the server-side finalize handler is idempotent — it
// returns success for an already-finalized transcript). The in-flight map
// dedupes concurrent finalize calls for the same transcript (e.g. a stop flow
// racing the recovery flow) so we never fire two finalizes at once.
const FINALIZE_ATTEMPTS = 3;
const FINALIZE_TIMEOUT_MS = 30 * 1000;
const FINALIZE_RETRY_BASE_DELAY_MS = 2000;
const finalizeInFlightByTranscriptId = new Map<string, Promise<void>>();

const finalizeUploadedRecordingOnce = async (itemId: string) => {
  if (!itemId) {
    throw new Error("Missing transcript id for the uploaded recording.");
  }

  const manifest = await getLiveRecordingRecoveryForTranscript(itemId);
  let lastErrorMessage: string | undefined;

  for (let attempt = 1; attempt <= FINALIZE_ATTEMPTS; attempt++) {
    const finalizeResponse = await actions.transcript.liveRecordingChunk(
      {
        action: "finalize",
        transcriptId: itemId,
        ...(manifest?.finalSequenceNumber && { finalSequenceNumber: manifest.finalSequenceNumber }),
      },
      { timeoutMs: FINALIZE_TIMEOUT_MS, keepalive: true },
    );

    // Only a typed success may clear the local recovery backup.
    if (!("error" in finalizeResponse)) {
      return;
    }

    lastErrorMessage = finalizeResponse.error;
    console.error(
      `Finalize attempt ${attempt}/${FINALIZE_ATTEMPTS} failed:`,
      lastErrorMessage || "no response",
    );

    if (attempt < FINALIZE_ATTEMPTS) {
      await new Promise((resolve) =>
        setTimeout(resolve, FINALIZE_RETRY_BASE_DELAY_MS * attempt),
      );
    }
  }

  throw new Error(
    lastErrorMessage ||
      "The recording was uploaded, but starting the transcription failed. It will finish automatically the next time you open the app.",
  );
};

const formatTimeToHHMMSS = (ms: number) => {
  if (!ms && ms !== 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const BAR_COUNT = 24;
const BAR_SHAPES: number[] = [];
for (let i = 0; i < BAR_COUNT; i++) {
  const d = Math.abs(i - BAR_COUNT / 2) / (BAR_COUNT / 2);
  BAR_SHAPES[i] = Math.exp(-4 * d * d);
}

const AudioLevelIndicator = memo(
  ({ level, isRecording }: { level: number; isRecording: boolean }) => {
    const clampedLevel = Math.min(1, Math.pow(level, 0.4) * 1.8);

    return (
      <div className="w-full flex items-center justify-center gap-1.5 h-24 px-4">
        {BAR_SHAPES.map((shape, i) => {
          const height = isRecording
            ? Math.max(4, clampedLevel * shape * 80)
            : Math.max(4, shape * 8);

          return (
            <div
              key={i}
              className="w-1.5 rounded-full transition-[height] duration-150 ease-out"
              style={{
                height: `${height}px`,
                backgroundColor: isRecording
                  ? "var(--text-primary)"
                  : "var(--glass-bg-active)",
              }}
            />
          );
        })}
      </div>
    );
  },
);

// Minimal Health Status Dot (memoized)
const HealthStatusIndicator = memo(
  ({ health }: { health: RecordingHealth | undefined }) => {
    if (!health) return null;

    const statusColor = {
      healthy: "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]",
      warning: "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]",
      error: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]",
    }[health.status];

    return <div className={`w-2 h-2 rounded-full ${statusColor}`} />;
  },
);

// Warning banner component
const getRecordingWarningMessage = ({
  health,
  status,
  t,
}: {
  health: RecordingHealth | undefined;
  status: string;
  t: (key: string) => string;
}) => {
  if (!health) return "";

  if (status === "uploading") {
    return t(
      "alertWidgetCreateTranscriptVoiceRecording.uploadFinishingWarning",
    );
  }

  if (health.isLocalBackupReady === false) {
    return t(
      "alertWidgetCreateTranscriptVoiceRecording.localBackupUnavailableWarning",
    );
  }

  if (health.hasDataLoss) {
    return t("alertWidgetCreateTranscriptVoiceRecording.dataLossWarning");
  }

  if (!health.isNetworkAvailable || health.hasNetworkError) {
    return t(
      "alertWidgetCreateTranscriptVoiceRecording.networkBackedUpWarning",
    );
  }

  if (health.pendingChunks > 3) {
    return t("alertWidgetCreateTranscriptVoiceRecording.uploadDelayWarning");
  }

  return health.lastErrorMessage || "";
};

const RecordingWarningBanner = ({
  health,
  status,
  t,
}: {
  health: RecordingHealth | undefined;
  status: string;
  t: (key: string) => string;
}) => {
  if (!health || health.status === "healthy") return null;

  const message = getRecordingWarningMessage({ health, status, t });

  if (!message) return null;

  const isError = health.status === "error";

  return (
    <div
      className={`
        flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium mb-4
        backdrop-blur-md border
        ${
          isError
            ? "bg-[var(--destructive-bg)] text-[var(--destructive)]"
            : "bg-[var(--premium-surface)] text-[var(--premium-primary)]"
        }
      `}
    >
      <span>{message}</span>
    </div>
  );
};

const getStopFailureSubtitle = ({
  error,
  t,
}: {
  error?: StopRecordingError;
  t: (key: string) => string;
}) => {
  if (error?.isLocalBackupReady === false) {
    return t(
      "alertWidgetCreateTranscriptVoiceRecording.saveFailedLocalBackupSubtitle",
    );
  }

  if (error?.hasDataLoss) {
    return t(
      "alertWidgetCreateTranscriptVoiceRecording.saveFailedDataLossSubtitle",
    );
  }

  return (
    error?.message ||
    t("alertWidgetCreateTranscriptVoiceRecording.saveFailedGenericSubtitle")
  );
};

const AlertWidgetCreateTranscriptVoiceRecording = () => {
  const host = useSharedDesktopHost();
  const visible = useUISlice(
    (state) => state.alertCreateTranscriptFromRecording,
  );
  const setVisible = useUISlice(
    (state) => state.setAlertCreateTranscriptFromRecording,
  );
  const router = useRouter();
  const width = useUISlice((state) => state.device.width);
  const recordingId = useUISlice((state) => state.liveRecording?.itemId);
  const transcriptionLanguage = useAppStore(
    (state) =>
      state.transcripts.find((item) => item.id === recordingId)?.language ||
      state.userData?.preferences?.transcriptionLanguage ||
      getDefaultTranscriptionLanguage(),
  );
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const isStoppingRef = useRef(false);
  const isStartFlowInFlightRef = useRef(false);
  const awaitingRecordingStartRef = useRef(false);
  // Set when the user explicitly aborts via the cancel/delete confirmation
  // while a stop is in flight. The stopRecording onError handler checks this
  // to suppress the save-failed modal in that case (the user already chose to
  // discard, so showing another modal would be confusing).
  const isAbortingRef = useRef(false);
  const hasBootstrappedVisibleRef = useRef(false);
  const recoveryPromiseRef = useRef<Promise<boolean> | null>(null);
  const shouldStartFreshAfterRecoveryRef = useRef(false);
  const visibleRef = useRef(Boolean(visible));
  const allowedAudioDurationInSeconds = 10 * 60 * 60; // 10 hours of recorded audio

  const liveRecorder = useLiveRecorder();
  const {
    status,
    elapsedTime,
    audioLevel,
    recordingHealth,
    startRecording,
    stopRecording,
    pauseOrPlay,
    cancelRecording,
  } = liveRecorder;
  const platform = getPlatform();
  // "warning" is a live session whose mic is silenced/unhealthy — keep showing
  // the recording controls (with the warning banner) instead of a loading state.
  const hasRecordingStarted =
    status === "recording" || status === "paused" || status === "warning";
  const shouldShowRecordingControls = !isLoading && hasRecordingStarted;
  const shouldShowLoadingState = isLoading || !hasRecordingStarted;
  const isAndroidUploading = platform === "android" && status === "uploading";

  useEffect(() => {
    visibleRef.current = Boolean(visible);
  }, [visible]);

  useEffect(() => {
    if (!awaitingRecordingStartRef.current || !hasRecordingStarted) {
      return;
    }

    awaitingRecordingStartRef.current = false;
    setIsLoading(false);
  }, [hasRecordingStarted]);

  const finalizeUploadedRecording = async (itemId: string) => {
    const inFlight = finalizeInFlightByTranscriptId.get(itemId);
    if (inFlight) {
      return inFlight;
    }

    const finalizePromise = finalizeUploadedRecordingOnce(itemId).finally(
      () => {
        finalizeInFlightByTranscriptId.delete(itemId);
      },
    );

    finalizeInFlightByTranscriptId.set(itemId, finalizePromise);
    return finalizePromise;
  };

  const finalizeRecordingAndOpenTranscript = async (itemId: string) => {
    await finalizeUploadedRecording(itemId);

    setIsLoading(false);
    useUISlice.getState().setAlertModal(undefined);
    useUISlice.getState().setLiveRecording(undefined);
    setVisible(undefined);

    await new Promise((resolve) => setTimeout(resolve, 300));
    router.push({
      pathname: "/transcript",
      search: `?${new URLSearchParams({ id: itemId })}`,
    });
  };

  const showRecoveryFailure = (
    transcriptId: string | undefined,
    subtitle?: string | null,
  ) => {
    setIsLoading(false);
    setVisible(undefined);
    useUISlice.getState().setLiveRecording(undefined);

    const retryBlockedRecovery = async () => {
      if (!transcriptId) return;
      shouldStartFreshAfterRecoveryRef.current = false;
      useUISlice.getState().setAlertModal({
        title: t("alertWidgetCreateTranscriptVoiceRecording.saveFailedTitle"),
        subtitle: t(
          "alertWidgetCreateTranscriptVoiceRecording.recoveryRetryingSubtitle",
        ),
        loading: true,
        hideCancel: true,
        hideDismissIcon: true,
        shouldPersistBackdropClick: true,
      });
      try {
        await recoverInterruptedRecording({
          retryBlocked: true,
          openTranscriptOnSuccess: true,
        });
      } catch {
        // recoverInterruptedRecording restores the recovery choices and keeps
        // the local backup intact when the retry cannot finish.
      }
    };

    const confirmPermanentDelete = () => {
      useUISlice.getState().setAlertModal({
        title: t(
          "alertWidgetCreateTranscriptVoiceRecording.recoveryDeleteTitle",
        ),
        subtitle: t(
          "alertWidgetCreateTranscriptVoiceRecording.recoveryDeleteSubtitle",
        ),
        cancelText: t("alertWidgetCreateTranscriptVoiceRecording.cancel"),
        onDismiss: () => showRecoveryFailure(transcriptId, subtitle),
        onLeftButtonPress: () => showRecoveryFailure(transcriptId, subtitle),
        button: {
          text: t(
            "alertWidgetCreateTranscriptVoiceRecording.recoveryDeleteConfirmButton",
          ),
          destructive: true,
          onPress: async () => {
            if (!transcriptId) return;
            useUISlice.getState().setAlertModal({
              title: t(
                "alertWidgetCreateTranscriptVoiceRecording.recoveryDeleteTitle",
              ),
              subtitle: t(
                "alertWidgetCreateTranscriptVoiceRecording.recoveryDeleteSubtitle",
              ),
              loading: true,
              hideCancel: true,
              hideDismissIcon: true,
              shouldPersistBackdropClick: true,
            });
            const result = await actions.transcript.deleteForever(transcriptId);
            if ("error" in result) {
              showRecoveryFailure(transcriptId, subtitle);
              return;
            }
            await liveRecorder.discardRecovery({ transcriptId });
            useUISlice.getState().setAlertModal(undefined);
          },
        },
      });
    };

    useUISlice.getState().setAlertModal({
      title: t("alertWidgetCreateTranscriptVoiceRecording.saveFailedTitle"),
      subtitle:
        subtitle ||
        t(
          "alertWidgetCreateTranscriptVoiceRecording.recoverySaveFailedSubtitle",
        ),
      cancelText: t(
        "alertWidgetCreateTranscriptVoiceRecording.recoveryDeleteButton",
      ),
      hideDismissIcon: true,
      shouldPersistBackdropClick: true,
      onLeftButtonPress: confirmPermanentDelete,
      button: {
        text: t(
          "alertWidgetCreateTranscriptVoiceRecording.recoveryRetryButton",
        ),
        onPress: retryBlockedRecovery,
      },
    });
  };

  const cleanupFailedRecordingStart = async (transcriptId?: string) => {
    awaitingRecordingStartRef.current = false;
    useUISlice.getState().setLiveRecording(undefined);
    setIsLoading(false);
    setVisible(undefined);

    if (!transcriptId) {
      return;
    }

    try {
      await actions.transcript.deleteForever(transcriptId);
    } catch (error) {
      console.error(
        "Failed to clean up transcript after recording start failure:",
        error,
      );
    }
  };

  const onStopRecording = async () => {
    // Guard against double-clicks / auto-stop racing the user. The recorder
    // layer is now idempotent too, but the UI guard avoids spurious extra
    // finalize calls and overlapping toast/modal updates.
    if (isStoppingRef.current) {
      return;
    }
    isStoppingRef.current = true;

    const itemId = useUISlice.getState().liveRecording?.itemId;
    if (!itemId) {
      isStoppingRef.current = false;
      throw new Error("Cannot stop recording without an active item id");
    }

    setIsLoading(true);

    await stopRecording({
      onSuccess: async () => {
        await finalizeRecordingAndOpenTranscript(itemId);
      },
      onError: async (error) => {
        console.error("Failed to stop recording");
        // If the user explicitly aborted while the stop was in flight (via the
        // cancel button on the spinner), the cancel flow has already set up
        // its own UI; don't pile a save-failed modal on top of it.
        if (isAbortingRef.current) {
          isAbortingRef.current = false;
          isStoppingRef.current = false;
          return;
        }
        useUISlice.getState().setLiveRecording(undefined);
        setIsLoading(false);
        setVisible(undefined);
        isStoppingRef.current = false;
        useUISlice.getState().setAlertModal({
          title: t("alertWidgetCreateTranscriptVoiceRecording.saveFailedTitle"),
          subtitle: getStopFailureSubtitle({ error, t }),
          hideCancel: true,
          button: {
            text: t(
              "alertWidgetCreateTranscriptVoiceRecording.understoodButton",
            ),
            onPress: () => {
              useUISlice.getState().setAlertModal(undefined);
            },
          },
        });
      },
    });
  };

  const onStartRecording = async () => {
    if (isStartFlowInFlightRef.current) {
      return;
    }

    isStartFlowInFlightRef.current = true;

    try {
      setIsLoading(true);

      if (desktop.isAvailable) {
        const permissions = await host.refreshPermissions();
        if (!visibleRef.current) return;
        const missing =
          permissions?.microphone !== "granted"
            ? "microphone"
            : permissions.systemAudio !== "granted"
              ? "systemAudio"
              : undefined;
        if (missing) {
          setIsLoading(false);
          setVisible(undefined);
          useUISlice.setState({ desktopDictationSetupRequested: missing });
          return;
        }
      }

      const created = await actions.transcript.create({
        type: "voice_recording",
        language: transcriptionLanguage,
        fileName: t("actions.recordingName", {
          date: new Date().toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        }),
      });
      if ("error" in created) throw createActionError(created.error);
      const transcriptId = created.value.id;
      try {
        // If the user dismissed the modal while the transcript was being
        // created, bail out before starting capture. Otherwise we'd start
        // a ghost recording with no UI and no cleanup path.
        if (!visibleRef.current) {
          await cleanupFailedRecordingStart(transcriptId);
          return;
        }

        useUISlice.getState().setLiveRecording({ itemId: transcriptId });
        const started = await startRecording();
        if (!started) {
          const recordingStatus = useUISlice.getState().liveRecording?.status;
          if (recordingStatus === "permissionDenied") {
            // OS denied the prompt - clear any optimistic flag so the
            // OS permission state remains authoritative next time.
            useAppStore.getState().setMicPermissionGranted(false);
          }
          await cleanupFailedRecordingStart(transcriptId);

          if (desktop.isAvailable && recordingStatus === "permissionDenied") {
            const denied = useUISlice.getState().desktopAudioPermissionDenied;
            useUISlice.setState({
              desktopDictationSetupRequested: denied?.microphone
                ? "microphone"
                : "systemAudio",
            });
            return;
          }

          if (
            recordingStatus === "error" ||
            recordingStatus === "permissionDenied"
          ) {
            toast.error(
              t(
                "alertWidgetCreateTranscriptVoiceRecording.failedToStartRecording",
              ),
            );
          }
          return;
        }

        // Recording actually started, so the OS granted the permission.
        // Remember the OS grant for recording recovery and settings.
        if (!useAppStore.getState().micPermissionGranted) {
          useAppStore.getState().setMicPermissionGranted(true);
        }
        if (!useAppStore.getState().notificationPermissionRequested) {
          void requestNotificationPermission();
        }

        const currentRecordingStatus =
          useUISlice.getState().liveRecording?.status;

        if (
          currentRecordingStatus === "recording" ||
          currentRecordingStatus === "paused"
        ) {
          awaitingRecordingStartRef.current = false;
          setIsLoading(false);
        } else {
          // Keep showing the spinner until the recorder confirms an
          // actual started state. This avoids flashing the controls
          // while the permission prompt / native startup is still pending.
          awaitingRecordingStartRef.current = true;
        }
      } catch (error) {
        console.error("Failed to start recording:", error);
        await cleanupFailedRecordingStart(transcriptId);
        toast.error(
          t("alertWidgetCreateTranscriptVoiceRecording.failedToStartRecording"),
        );
      }
    } catch (error) {
      console.error("Failed to create transcript:", error);
      awaitingRecordingStartRef.current = false;
      setIsLoading(false);
      toast.error(
        t("alertWidgetCreateTranscriptVoiceRecording.failedToStartRecording"),
      );
    } finally {
      isStartFlowInFlightRef.current = false;
    }
  };

  // Best-effort: request the OS notification permission. We never fail the
  // recording start when this is denied - notifications are optional, but on
  // Android they let the foreground service stay alive in the background.
  const requestNotificationPermission = async () => {
    try {
      const notifications = getPlatformRuntime().notifications;
      const current = await notifications.checkPermission();
      if (current !== "granted") {
        await notifications.requestPermission();
      }
    } catch (error) {
      console.error("Failed to request notification permission:", error);
    } finally {
      // Mark as requested regardless of outcome so we don't re-prompt the user
      // every time they start a new recording. They can re-grant via OS settings.
      useAppStore.getState().setNotificationPermissionRequested(true);
    }
  };

  const startFreshRecordingFlow = () => {
    if (desktop.isAvailable) {
      setVisible(undefined);
      useUISlice.setState({ desktopRecordRequested: true });
      return;
    }
    onStartRecording();
  };

  const recoverInterruptedRecording = async (
    options: {
      retryBlocked?: boolean;
      openTranscriptOnSuccess?: boolean;
    } = {},
  ) => {
    if (recoveryPromiseRef.current) {
      return recoveryPromiseRef.current;
    }

    let recoveryRecovered = false;
    // Capture the transcriptId from whichever callback fires first so the
    // catch block can pass it to showRecoveryFailure even when the error
    // occurs before onRecoveryFailed runs.
    let lastKnownRecoveryTranscriptId: string | undefined;

    const recoveryPromise = (async () => {
      try {
        const recoveredIds = new Set<string>();
        let handled = false;
        while (true) {
          const recoveryResult = await recoverInterruptedLiveRecording({
            recorder: liveRecorder,
            excludeTranscriptIds: [...recoveredIds],
            retryBlocked: options.retryBlocked,
            onDiscardEmpty: async ({ transcriptId }) => {
              lastKnownRecoveryTranscriptId = transcriptId;
              await actions.transcript.deleteForever(transcriptId);
            },
            onRecoveryStarted: async ({ transcriptId }) => {
              lastKnownRecoveryTranscriptId = transcriptId;
              if (visibleRef.current) {
                setIsLoading(true);
              }
            },
            onRecoveryBlocked: async ({ recoveryStatus, transcriptId }) => {
              lastKnownRecoveryTranscriptId = transcriptId;
              if (visibleRef.current || options.retryBlocked) {
                showRecoveryFailure(
                  transcriptId,
                  recoveryStatus.lastErrorMessage,
                );
              }
            },
            onRecoveryFailed: async ({ transcriptId, error }) => {
              lastKnownRecoveryTranscriptId = transcriptId;
              console.error("Recovery upload failed:", error);
              if (visibleRef.current || options.retryBlocked) {
                showRecoveryFailure(transcriptId);
              }
            },
            onRecovered: async ({ transcriptId }) => {
              if (options.openTranscriptOnSuccess) {
                await finalizeRecordingAndOpenTranscript(transcriptId);
                return;
              }
              await finalizeUploadedRecording(transcriptId);
            },
          });

          handled ||= recoveryResult.recovered || recoveryResult.blocked === true;
          recoveryRecovered ||= recoveryResult.recovered;
          if (!recoveryResult.transcriptId || recoveredIds.has(recoveryResult.transcriptId)) return handled;
          recoveredIds.add(recoveryResult.transcriptId);
        }
      } catch (error) {
        console.error("Failed to recover interrupted recording:", error);
        if (visibleRef.current || options.retryBlocked) {
          showRecoveryFailure(lastKnownRecoveryTranscriptId);
        }
        throw error;
      }
    })().finally(() => {
      recoveryPromiseRef.current = null;

      if (
        recoveryRecovered &&
        shouldStartFreshAfterRecoveryRef.current &&
        visibleRef.current
      ) {
        shouldStartFreshAfterRecoveryRef.current = false;
        void startFreshRecordingFlow();
        return;
      }

      if (visibleRef.current) {
        setIsLoading(false);
      }
    });

    recoveryPromiseRef.current = recoveryPromise;
    return recoveryPromise;
  };

  const onDeleteRecording = () => {
    if (isAndroidUploading) {
      useUISlice.getState().setAlertModal({
        title: t("alertWidgetCreateTranscriptVoiceRecording.title"),
        subtitle: t(
          "alertWidgetCreateTranscriptVoiceRecording.uploadFinishingWarning",
        ),
        hideCancel: true,
        button: {
          text: t("alertWidgetCreateTranscriptVoiceRecording.understoodButton"),
          onPress: () => {
            useUISlice.getState().setAlertModal(undefined);
          },
        },
      });
      return;
    }

    // Allow the user to abort even during the stop spinner. The cancelRecording
    // path tears down the recorder, which rejects any in-flight stop wait, and
    // the isAbortingRef guard suppresses the save-failed modal that would
    // otherwise pile on top of the cancel confirmation.
    useUISlice.getState().setAlertModal({
      title: t(
        "alertWidgetCreateTranscriptVoiceRecording.deleteRecordingTitle",
      ),
      subtitle: t(
        "alertWidgetCreateTranscriptVoiceRecording.deleteRecordingSubtitle",
      ),
      cancelText: t("alertWidgetCreateTranscriptVoiceRecording.cancel"),
      button: {
        text: t("alertWidgetCreateTranscriptVoiceRecording.deleteButton"),
        destructive: true,
        onPress: () => {
          // Only set the abort flag when a STOP is genuinely in flight.
          // isLoading alone is not reliable (it's also true during startup
          // and recovery), so gate on isStoppingRef too.
          if (isStoppingRef.current) {
            isAbortingRef.current = true;
          }
          useUISlice.getState().setAlertModal(undefined);

          cancelRecording(async (result) => {
            isStoppingRef.current = false;

            if (!result.success) {
              // The native recorder may still be running with audio queued
              // for upload. Keep the UI open so the user retains control
              // over the still-active session instead of losing all handles.
              isAbortingRef.current = false;
              toast.error(
                t(
                  "alertWidgetCreateTranscriptVoiceRecording.saveFailedGenericSubtitle",
                ),
              );
              return;
            }

            // Cancel succeeded — safe to tear down UI and clean up.
            setVisible(undefined);
            const itemId = useUISlice.getState().liveRecording?.itemId;
            useUISlice.getState().setLiveRecording(undefined);
            isAbortingRef.current = false;

            if (itemId) {
              try {
                await liveRecorder.discardRecovery({ transcriptId: itemId });
              } catch (error) {
                console.error("Failed to discard recovery on cancel:", error);
              }
              void actions.transcript.deleteForever(itemId);
            }
          });
        },
      },
    });
  };

  // Auto-stop when recorded audio (not wall-clock) exceeds the allowed duration.
  // elapsedTime from the recorder only counts active recording, not paused time,
  // so a long pause doesn't trigger an early stop.
  useEffect(() => {
    if (
      (status === "recording" || status === "paused" || status === "warning") &&
      elapsedTime >= allowedAudioDurationInSeconds * 1000 &&
      !isStoppingRef.current
    ) {
      void onStopRecording();
    }
    // We deliberately depend only on the values that should re-evaluate the
    // auto-stop. onStopRecording is captured from the latest closure on each
    // render so it sees up-to-date isStoppingRef state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedTime, status]);

  const desktopStopRequested = useUISlice(
    (state) => state.desktopRecordingStopRequested,
  );
  useEffect(() => {
    if (!desktopStopRequested) return;
    useUISlice.setState({ desktopRecordingStopRequested: false });
    if (hasRecordingStarted) void onStopRecording().catch(() => undefined);
    // The same guarded stop/finalize flow as the visible recording sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopStopRequested, hasRecordingStarted]);

  const desktopPauseRequested = useUISlice(
    (state) => state.desktopRecordingPauseRequested,
  );
  useEffect(() => {
    if (!desktopPauseRequested) return;
    useUISlice.setState({ desktopRecordingPauseRequested: undefined });
    if (isStoppingRef.current) return;
    if (
      (desktopPauseRequested === "pause" && status === "recording") ||
      (desktopPauseRequested === "resume" && status === "paused")
    ) void pauseOrPlay();
  }, [desktopPauseRequested, status, pauseOrPlay]);

  const recoveryRunnerRef = useRef(recoverInterruptedRecording);
  recoveryRunnerRef.current = recoverInterruptedRecording;
  useEffect(() => {
    let disposed = false;
    const retry = () => {
      const ui = useUISlice.getState();
      if (disposed || (ui.liveRecording && ui.liveRecording.status !== "error") || ui.desktopCaptureActive || !useAppStore.getState().userToken) return;
      void recoveryRunnerRef.current().catch(() => undefined);
    };
    retry();
    const appStateListener = onRecordingAppStateChange(({ isActive }) => { if (isActive) retry(); }).catch(() => undefined);
    const timer = setInterval(retry, 5 * 60 * 1000);
    const removeNetwork = attachRecordingNetworkListeners(window, { online: retry, offline: () => undefined });
    const removeVisibility = attachRecordingVisibilityListener(document, () => { if (document.visibilityState === "visible") retry(); });
    return () => {
      disposed = true;
      clearInterval(timer);
      removeNetwork();
      removeVisibility();
      void appStateListener.then(listener => listener?.remove()).catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    if (!visible) {
      hasBootstrappedVisibleRef.current = false;
      shouldStartFreshAfterRecoveryRef.current = false;
      awaitingRecordingStartRef.current = false;
      isStoppingRef.current = false;
      setIsLoading(true);
      return;
    }

    if (hasBootstrappedVisibleRef.current) {
      return;
    }

    hasBootstrappedVisibleRef.current = true;
    isStoppingRef.current = false;

    const bootstrap = async () => {
      setIsLoading(true);

      shouldStartFreshAfterRecoveryRef.current = true;

      try {
        const handledRecovery = await recoverInterruptedRecording();
        if (handledRecovery) {
          return;
        }
      } catch (error) {
        console.error("Visible recovery bootstrap failed:", error);
        shouldStartFreshAfterRecoveryRef.current = false;
        return;
      }

      shouldStartFreshAfterRecoveryRef.current = false;
      startFreshRecordingFlow();
    };

    void bootstrap();
    // recoverInterruptedRecording and startFreshRecordingFlow are recreated on
    // every render but read all their dependencies via refs and getState() at
    // call time. Adding them to deps would cause the bootstrap to re-run on
    // every render, which is exactly what hasBootstrappedVisibleRef is meant
    // to prevent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={t("alertWidgetCreateTranscriptVoiceRecording.title")}
      onDismiss={onDeleteRecording}
      hideDismissIcon={isAndroidUploading}
      hideCancel
      modalWidth={Math.min(width, 500)} // Slightly narrower for more elegance
    >
      <div className="flex flex-col items-center w-full">
        {/* Language Selector - Glass Style. Keep this available while actively
            recording so the user can change the transcription language for the
            current live-recording session before it is finalized. */}
        <button
          data-action-id="recording.language.open"
          disabled={isLoading || status === "uploading"}
          onClick={() =>
            useUISlice.getState().setAlertLanguagePicker({
              selectedLanguage: transcriptionLanguage,
              onSelect: (languageCode) => {
                void actions.user
                  .changeTranscriptionLanguage(
                    languageCode,
                    undefined,
                    useUISlice.getState().liveRecording?.itemId,
                  )
                  .then((result) => {
                    if ("error" in result)
                      toast.error(t("settings.dictationLanguageSaveError"));
                  });
              },
            })
          }
          className="mb-8 flex items-center gap-3 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)] text-base font-medium rounded-full pl-6 pr-5 py-3 transition-all cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--bg-secondary)] disabled:hover:shadow-sm"
        >
          <span>
            {LANGUAGES.find((l) => l.value === transcriptionLanguage)?.label ||
              transcriptionLanguage}
          </span>
          <ChevronDownIcon className="text-[var(--text-tertiary)]" />
        </button>

        {/* Warning banner */}
        {shouldShowRecordingControls && (
          <RecordingWarningBanner
            health={recordingHealth}
            status={status}
            t={t}
          />
        )}

        {shouldShowRecordingControls && (
          <div className="w-full flex flex-col items-center gap-8">
            {/* Audio Visualization */}
            <div className="w-full relative">
              <AudioLevelIndicator
                level={audioLevel ?? 0}
                isRecording={status === "recording"}
              />

              {/* Floating Timer overlay or just centered below */}
              <div className="absolute -bottom-8 left-0 right-0 flex justify-center items-center gap-2">
                <HealthStatusIndicator health={recordingHealth} />
                <span className="text-[var(--text-tertiary)] text-sm font-mono tracking-wider">
                  {formatTimeToHHMMSS(elapsedTime)}
                </span>
              </div>
            </div>
            <div className="h-4" /> {/* Spacer */}
            {/* Glass Controls */}
            <div className="flex items-center gap-6">
              {/* Play/Pause Button */}
              <button
                data-action-id="recording.pauseToggle"
                onClick={pauseOrPlay}
                className="w-14 h-14 rounded-full flex items-center justify-center glass-tile hover:bg-[var(--glass-bg-hover)] backdrop-blur-md transition-all active:scale-95 group"
                aria-label={
                  status !== "paused"
                    ? t("alertWidgetCreateTranscriptVoiceRecording.pause")
                    : t("alertWidgetCreateTranscriptVoiceRecording.resume")
                }
              >
                {status !== "paused" ? (
                  <PauseIcon className="text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors" />
                ) : (
                  <PlayIcon className="text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors" />
                )}
              </button>

              {/* Stop Button - Prominent but minimal */}
              <button
                data-action-id="recording.stop"
                onClick={onStopRecording}
                aria-label={t(
                  "alertWidgetCreateTranscriptVoiceRecording.stopButton",
                )}
                className="w-20 h-20 rounded-full flex items-center justify-center bg-[var(--glass-bg-active)] hover:bg-[var(--glass-bg-hover)] backdrop-blur-sm transition-all active:scale-95 shadow-lg shadow-black/20 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-rose-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-8 h-8 rounded-md bg-rose-500 shadow-lg shadow-rose-500/40 transition-transform group-hover:scale-110" />
              </button>

              {/* Cancel Button (added for UX) or just spacing if relying on modal close */}
              <div className="w-14 h-14 flex items-center justify-center">
                {/* Placeholder for balance */}
              </div>
            </div>
          </div>
        )}

        {shouldShowLoadingState && (
          <div className="flex flex-col items-center justify-center h-[200px] w-full gap-4">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full shadow-[inset_0_0_0_2px_var(--fill-3)]"></div>
              <div className="absolute inset-0 rounded-full border-t-2 border-[var(--text-primary)] animate-spin"></div>
            </div>
            {status === "uploading" && (
              <p className="text-center text-sm text-[var(--text-secondary)] max-w-[280px]">
                {t(
                  "alertWidgetCreateTranscriptVoiceRecording.uploadFinishingWarning",
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

export default AlertWidgetCreateTranscriptVoiceRecording;
